<template>
  <div class="seller-layout">
    <!-- 헤더 -->
    <header class="seller-header">
      <!-- 상단 유틸리티 바 -->
      <div class="header-utility-bar">
        <div class="utility-left">
          <span class="system-status">
            <i class="bi bi-circle-fill status-dot"></i>
            입점사 센터
          </span>
        </div>
        <div class="utility-right">
          <span class="current-time">
            <i class="bi bi-clock"></i>
            {{ currentTime }}
          </span>
          <router-link to="/seller/notifications" class="bell" title="알림">
            <i class="bi bi-bell"></i>
            <span v-if="unreadCount > 0" class="bell-badge">{{ unreadCount }}</span>
          </router-link>
          <span class="seller-name">
            <i class="bi bi-shop"></i>
            {{ sellerName }}
          </span>
          <button class="utility-btn" @click="logout">
            <i class="bi bi-box-arrow-right"></i>
            로그아웃
          </button>
        </div>
      </div>

      <!-- 메인 헤더 -->
      <div class="header-main">
        <div class="header-brand">
          <router-link to="/seller" class="brand-link">
            <div class="brand-icon">
              <i class="bi bi-shop-window"></i>
            </div>
            <div class="brand-text">
              <span class="brand-title">Doctor Market</span>
              <span class="brand-subtitle">Seller Center</span>
            </div>
          </router-link>
        </div>

        <!-- 대분류 네비게이션 -->
        <nav class="header-nav">
          <div class="nav-items">
            <router-link to="/seller" class="nav-item" :class="{ active: isDashboard }">
              <i class="bi bi-speedometer2"></i><span>대시보드</span>
            </router-link>
            <a
              v-for="(menu, index) in mainMenus"
              :key="index"
              href="#"
              class="nav-item"
              :class="{ active: activeMenu === menu.id }"
              @click.prevent="setActiveMenu(menu)"
            >
              <i :class="menu.icon"></i><span>{{ menu.name }}</span>
            </a>
          </div>
        </nav>

        <button class="mobile-menu-toggle" @click="toggleMobileSidebar">
          <i class="bi bi-list"></i>
        </button>
      </div>
    </header>

    <!-- 메인 컨테이너 -->
    <div class="seller-main-container">
      <!-- 모바일 사이드바 배경 오버레이 -->
      <div class="sidebar-overlay" v-if="showMobileSidebar" @click="showMobileSidebar = false"></div>

      <!-- 사이드바 (중분류) -->
      <aside class="seller-sidebar" :class="{ show: showMobileSidebar }">
        <!-- 데스크톱: 현재 대분류의 중분류 -->
        <!-- 🔥 2026-07-28 R6: 대분류 미선택(대시보드)일 때는 좌측 헤더도 공란 처리 -->
        <div class="sidebar-header desktop-only" v-if="activeMenu">
          <i :class="getActiveMenuIcon()"></i>
          <span>{{ getActiveMenuName() }}</span>
        </div>
        <nav class="sidebar-nav desktop-only">
          <router-link
            v-for="(sub, index) in getSubMenus()"
            :key="index"
            :to="sub.path"
            class="sidebar-link"
            active-class="active"
            @click="showMobileSidebar = false"
          >
            <i :class="sub.icon"></i>
            <span>{{ sub.name }}</span>
            <i class="bi bi-chevron-right arrow-icon"></i>
          </router-link>
        </nav>

        <!-- 모바일: 전체 대분류 + 중분류 트리 -->
        <nav class="sidebar-tree mobile-only">
          <router-link to="/seller" class="tree-dashboard" :class="{ active: isDashboard }" @click="showMobileSidebar = false">
            <i class="bi bi-speedometer2"></i> 대시보드
          </router-link>
          <div v-for="m in mainMenus" :key="m.id" class="tree-group">
            <div class="tree-group-head"><i :class="m.icon"></i> {{ m.name }}</div>
            <router-link
              v-for="(sub, i) in subMenus[m.id]"
              :key="i"
              :to="sub.path"
              class="sidebar-link"
              active-class="active"
              @click="showMobileSidebar = false"
            >
              <i :class="sub.icon"></i>
              <span>{{ sub.name }}</span>
            </router-link>
          </div>
        </nav>

        <div class="sidebar-footer desktop-only">
          <div class="stat-item">
            <i class="bi bi-shield-check"></i>
            <span>입점사 전용 시스템</span>
          </div>
        </div>
      </aside>

      <!-- 메인 콘텐츠 -->
      <main class="seller-content">
        <div class="content-wrapper">
          <router-view />
        </div>
      </main>
    </div>

    <!-- 푸터 -->
    <footer class="seller-footer">
      <div class="footer-content">
        <div class="footer-brand">
          <i class="bi bi-shop-window"></i>
          <div class="footer-brand-text">
            <span class="footer-title">Doctor Market Seller Center</span>
            <span class="footer-subtitle">입점사 통합 관리 시스템</span>
          </div>
        </div>
        <p class="copyright">© 2026 Doctor Market. All rights reserved.</p>
      </div>
    </footer>
  </div>
</template>

<script>
// 2026-09-04 R70: 상단 알림 아이콘 개수를 알림센터와 같은 기준으로 계산한다.
import { unreadCount as countUnreadNotifications } from '@/utils/sellerNotifications.js'

export default {
  name: 'SellerLayout',
  data() {
    return {
      showMobileSidebar: false,
      currentTime: '',
      sellerName: '입점사',
      unreadCount: 0,
      activeMenu: 'product',
      mainMenus: [
        { id: 'product', name: '상품관리', icon: 'bi bi-box-seam' },
        { id: 'order', name: '주문관리', icon: 'bi bi-cart-check' },
        { id: 'shipping', name: '배송관리', icon: 'bi bi-truck' },
        { id: 'settlement', name: '정산관리', icon: 'bi bi-cash-coin' },
        { id: 'store', name: '스토어관리', icon: 'bi bi-shop-window' },
        { id: 'marketing', name: '마케팅', icon: 'bi bi-megaphone' },
        { id: 'stats', name: '통계', icon: 'bi bi-graph-up' },
        { id: 'support', name: '고객지원', icon: 'bi bi-headset' },
        { id: 'account', name: '내정보', icon: 'bi bi-gear' }
      ],
      subMenus: {
        product: [
          { name: '상품 목록', path: '/seller/products', icon: 'bi bi-grid me-2' },
          { name: '상품 등록', path: '/seller/products/register', icon: 'bi bi-plus-circle me-2' },
          { name: '상품 일괄등록', path: '/seller/products/bulk', icon: 'bi bi-file-earmark-arrow-up me-2' },
          { name: '본사 카테고리 매칭', path: '/seller/products/category-mapping', icon: 'bi bi-diagram-3 me-2' }
        ],
        order: [
          { name: '주문 목록', path: '/seller/orders', icon: 'bi bi-list-ul me-2' },
          // 🔥 2026-07-28 R37: 사이트 내 반품·교환 기능이 없어 '클레임 관리'로 메뉴명 변경
          { name: '클레임 관리', path: '/seller/claims', icon: 'bi bi-arrow-return-left me-2' }
        ],
        shipping: [
          { name: '배송 현황', path: '/seller/shipping', icon: 'bi bi-truck me-2' },
          { name: '송장 등록', path: '/seller/shipping/invoice', icon: 'bi bi-receipt-cutoff me-2' },
          { name: '배송비 정책', path: '/seller/shipping/policy', icon: 'bi bi-sliders me-2' }
        ],
        settlement: [
          { name: '정산 내역', path: '/seller/settlement', icon: 'bi bi-cash-coin me-2' },
          { name: '세금계산서·증빙', path: '/seller/settlement/tax', icon: 'bi bi-file-earmark-text me-2' }
        ],
        store: [
          // 🔥 2026-07-28 R56: '스토어 설정(로고·소개)' 전체 기능 미사용 → 메뉴에서 숨김.
          //    라우트(/seller/store/settings)와 StoreSettings.vue는 그대로 보존, 아래 줄만 되살리면 복구됨.
          // { name: '스토어 설정(로고·소개)', path: '/seller/store/settings', icon: 'bi bi-shop me-2' },
          { name: '배너·상품 진열', path: '/seller/store/display', icon: 'bi bi-images me-2' }
        ],
        marketing: [
          { name: '광고 신청', path: '/seller/ads', icon: 'bi bi-megaphone me-2' }
        ],
        stats: [
          { name: '매출·통계 리포트', path: '/seller/stats', icon: 'bi bi-graph-up me-2' }
        ],
        support: [
          { name: '고객 문의(Q&A)', path: '/seller/inquiries', icon: 'bi bi-chat-dots me-2' },
          { name: '커뮤니티·공지', path: '/seller/community', icon: 'bi bi-people me-2' },
          { name: '알림 센터', path: '/seller/notifications', icon: 'bi bi-bell me-2' }
        ],
        account: [
          { name: '내 정보·정책', path: '/seller/account', icon: 'bi bi-building me-2' }
        ]
      }
    }
  },
  computed: {
    isDashboard() {
      return this.$route.path === '/seller'
    }
  },
  methods: {
    logout() {
      if (confirm('정말 로그아웃 하시겠습니까?')) {
        // 🔥 2026-07-28 R3: sessionStorage 기준. 구버전 localStorage 잔재도 함께 정리
        sessionStorage.removeItem('sellerToken')
        sessionStorage.removeItem('seller_info')
        localStorage.removeItem('sellerToken')
        localStorage.removeItem('seller_info')
        window.location.href = '/seller/login'
      }
    },
    setActiveMenu(menu) {
      this.activeMenu = menu.id
      const subs = this.getSubMenus()
      if (subs.length > 0) this.$router.push(subs[0].path)
    },
    getSubMenus() { return this.subMenus[this.activeMenu] || [] },
    getActiveMenuName() {
      const m = this.mainMenus.find(x => x.id === this.activeMenu)
      return m ? m.name : ''
    },
    getActiveMenuIcon() {
      const m = this.mainMenus.find(x => x.id === this.activeMenu)
      return m ? m.icon : 'bi bi-folder'
    },
    toggleMobileSidebar() {
      this.showMobileSidebar = !this.showMobileSidebar
    },
    updateTime() {
      const now = new Date()
      const options = {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
      }
      this.currentTime = now.toLocaleString('ko-KR', options)
    },
    loadSellerName() {
      try {
        // 🔥 2026-07-28 R3: seller_info는 sessionStorage에 저장된다
        const info = JSON.parse(sessionStorage.getItem('seller_info') || '{}')
        if (info && info.name) this.sellerName = info.name
      } catch (e) { /* 무시 */ }
    },
    syncActiveMenuFromRoute() {
      const path = this.$route.path
      for (const id in this.subMenus) {
        if (this.subMenus[id].some(s => path.startsWith(s.path))) { this.activeMenu = id; return }
      }
      // 🔥 2026-07-28 R6·R7: 매칭되는 중분류가 없으면(=대시보드) 대분류 선택을 해제한다.
      //    기존에는 초기값 'product'가 그대로 남아 대시보드에서도
      //    (R6) 좌측에 '상품관리' 중분류가 계속 보이고,
      //    (R7) 상단에서 '대시보드'와 '상품관리'가 동시에 선택된 것처럼 보였다.
      this.activeMenu = ''
    },
    // 2026-09-04 R70: 안읽은 알림 개수 계산. 실패해도 화면에는 영향 주지 않는다.
    async refreshUnread() {
      try { this.unreadCount = await countUnreadNotifications(this.$axios) } catch (e) { this.unreadCount = 0 }
    }
  },
  watch: {
    '$route'() {
      this.syncActiveMenuFromRoute()
      this.refreshUnread()   // 2026-09-04 R70: 화면 이동 시 개수 갱신
    }
  },
  created() {
    this.loadSellerName()
    this.syncActiveMenuFromRoute()
    this.updateTime()
    this.timeInterval = setInterval(this.updateTime, 1000)
    // 2026-09-04 R70: 알림센터에서 읽음 처리하면 즉시 배지를 다시 센다.
    this.refreshUnread()
    this.onNotiChanged = () => this.refreshUnread()
    window.addEventListener('seller-noti-changed', this.onNotiChanged)
  },
  beforeUnmount() {
    if (this.timeInterval) clearInterval(this.timeInterval)
    if (this.onNotiChanged) window.removeEventListener('seller-noti-changed', this.onNotiChanged)
  }
}
</script>

<style scoped>
* { box-sizing: border-box; margin: 0; padding: 0; }

.seller-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f5f6f7;
  font-family: Arial, "Helvetica Neue", "Malgun Gothic", sans-serif;
}

/* ===== 헤더 ===== */
.seller-header { position: sticky; top: 0; z-index: 1000; background: #fff; border-bottom: 1px solid #c0c0c0; }

.header-utility-bar {
  display: flex; justify-content: space-between; align-items: center;
  padding: 5px 20px;
  background: #1d2d3e;
  color: #c0c6cc; font-size: 11.5px;
}
.utility-left { display: flex; align-items: center; gap: 15px; }
.system-status { display: flex; align-items: center; gap: 6px; }
.status-dot { font-size: 8px; color: #8fbf6f; }
.utility-right { display: flex; align-items: center; gap: 18px; }
.current-time, .seller-name { display: flex; align-items: center; gap: 6px; }
.current-time i, .seller-name i { color: #9aa4ae; }
.utility-btn {
  display: flex; align-items: center; gap: 6px; padding: 3px 10px;
  background: transparent; border: 1px solid rgba(255,255,255,0.25);
  border-radius: 0; color: #d6dadf; font-size: 11.5px; cursor: pointer; transition: all 0.1s;
}
.utility-btn:hover { background: rgba(255,255,255,0.12); }
.bell { position: relative; display: flex; align-items: center; color: #d6dadf; text-decoration: none; font-size: 15px; }
.bell:hover { color: #fff; }
.bell-badge { position: absolute; top: -6px; right: -8px; min-width: 15px; height: 15px; padding: 0 3px; background: #d9534f; color: #fff; font-size: 9.5px; font-weight: 700; border-radius: 8px; display: flex; align-items: center; justify-content: center; line-height: 1; }

.header-main {
  display: flex; align-items: center; padding: 0 20px; height: 52px;
  background: #354a5f;
}
.header-brand { flex-shrink: 0; }
.brand-link { display: flex; align-items: center; gap: 11px; text-decoration: none; color: #fff; }
.brand-icon {
  width: 34px; height: 34px; background: rgba(255,255,255,0.12); border-radius: 0;
  display: flex; align-items: center; justify-content: center; font-size: 18px;
}
.brand-text { display: flex; flex-direction: column; }
.brand-title { font-size: 16px; font-weight: 700; letter-spacing: -0.3px; }
.brand-subtitle { font-size: 9.5px; opacity: 0.8; text-transform: uppercase; letter-spacing: 1px; }

/* 대분류 네비게이션 */
.header-nav { flex: 1; margin-left: 28px; overflow-x: auto; align-self: stretch; }
.header-nav::-webkit-scrollbar { display: none; }
.header-nav { -ms-overflow-style: none; scrollbar-width: none; }
.nav-items { display: flex; gap: 0; height: 100%; }
.nav-item {
  display: flex; align-items: center; gap: 6px; padding: 0 16px; height: 52px;
  color: rgba(255,255,255,0.82); text-decoration: none; font-size: 12.5px; font-weight: 500;
  border-radius: 0; transition: all 0.1s; white-space: nowrap; border-bottom: 3px solid transparent;
}
.nav-item:hover { background: rgba(255,255,255,0.1); color: #fff; }
.nav-item.active { background: rgba(0,0,0,0.18); color: #fff; border-bottom: 3px solid #fff; font-weight: 700; }
.nav-item i { font-size: 14px; }

.mobile-menu-toggle {
  display: none; padding: 7px 11px; background: rgba(255,255,255,0.12);
  border: none; border-radius: 0; color: #fff; font-size: 20px; cursor: pointer; margin-left: auto;
}

@media (max-width: 1100px) {
  .nav-item span { display: none; }
  .nav-item { padding: 0 12px; }
}
@media (max-width: 992px) {
  .header-nav { display: none; }
}

/* ===== 메인 컨테이너 ===== */
.seller-main-container { display: flex; flex: 1; min-height: calc(100vh - 150px); }

/* ===== 사이드바 ===== */
.seller-sidebar { width: 230px; background: #fafafa; border-right: 1px solid #d9d9d9; display: flex; flex-direction: column; flex-shrink: 0; }
.sidebar-header {
  display: flex; align-items: center; gap: 9px; padding: 14px 16px;
  background: #eaecee;
  border-bottom: 1px solid #d9d9d9; font-size: 13px; font-weight: 700; color: #32363a;
}
.sidebar-header i { font-size: 16px; color: #32363a; }
.sidebar-nav { flex: 1; padding: 6px; overflow-y: auto; }
.sidebar-link {
  display: flex; align-items: center; gap: 10px; padding: 10px 12px; color: #45494d;
  text-decoration: none; font-size: 12.5px; border-radius: 0; margin-bottom: 1px; transition: all 0.1s;
  border-left: 3px solid transparent;
}
.sidebar-link i:first-child { font-size: 15px; width: 18px; text-align: center; color: #6a6d70; transition: color 0.1s; }
.sidebar-link span { flex: 1; }
.sidebar-link .arrow-icon { font-size: 9px; opacity: 0; transition: all 0.1s; color: #9aa0a6; }
.sidebar-link:hover { background: #eef0f1; color: #1d2d3e; }
.sidebar-link:hover i:first-child { color: #32363a; }
.sidebar-link:hover .arrow-icon { opacity: 1; }
.sidebar-link.active { background: #354a5f; color: #fff; border-left: 3px solid #1d2d3e; }
.sidebar-link.active i:first-child { color: #fff; }
.sidebar-link.active .arrow-icon { opacity: 1; color: rgba(255,255,255,0.7); }
.sidebar-footer { padding: 12px 16px; border-top: 1px solid #d9d9d9; background: #eef0f1; }
.stat-item { display: flex; align-items: center; justify-content: center; gap: 6px; font-size: 11.5px; color: #6a6d70; }
.stat-item i { color: #6a6d70; }

/* ===== 콘텐츠 ===== */
.seller-content { flex: 1; min-width: 0; background: #f5f6f7; overflow-x: auto; }
.content-wrapper { padding: 20px; min-height: 100%; }

/* ===== 푸터 ===== */
.seller-footer { background: #1d2d3e; border-top: 1px solid #000; padding: 14px 20px; margin-top: auto; }
.footer-content { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px; }
.footer-brand { display: flex; align-items: center; gap: 11px; color: #fff; }
.footer-brand > i { font-size: 24px; color: #9aa4ae; }
.footer-brand-text { display: flex; flex-direction: column; }
.footer-title { font-size: 14px; font-weight: 600; }
.footer-subtitle { font-size: 10.5px; color: #9aa4ae; letter-spacing: 0.3px; }
.copyright { font-size: 10.5px; color: #6a737d; margin: 0; }

/* ===== 데스크톱/모바일 메뉴 토글 ===== */
.mobile-only { display: none; }
.sidebar-overlay { display: none; }

/* 모바일 전체 메뉴 트리 */
.sidebar-tree { padding: 4px 0; overflow-y: auto; flex: 1; }
.tree-dashboard {
  display: flex; align-items: center; gap: 8px; padding: 11px 14px; margin: 4px 6px;
  font-size: 13px; font-weight: 700; color: #32363a; text-decoration: none; background: #eef0f1; border-left: 3px solid #354a5f;
}
.tree-dashboard.active { background: #354a5f; color: #fff; }
.tree-group { border-top: 1px solid #ececec; padding: 4px 0; }
.tree-group-head {
  display: flex; align-items: center; gap: 7px; padding: 8px 14px 4px;
  font-size: 11px; font-weight: 700; color: #6a6d70; text-transform: uppercase; letter-spacing: 0.5px;
}
.tree-group-head i { font-size: 13px; }

/* ===== 반응형 ===== */
@media (max-width: 992px) {
  .desktop-only { display: none !important; }
  .mobile-only { display: block; }
  .mobile-menu-toggle { display: flex; }
  .seller-sidebar {
    position: fixed; left: 0; top: 0; bottom: 0; z-index: 1101; width: 264px;
    transform: translateX(-100%); transition: transform 0.2s; box-shadow: 2px 0 12px rgba(0,0,0,0.25);
  }
  .seller-sidebar.show { transform: translateX(0); }
  .sidebar-overlay { display: block; position: fixed; inset: 0; background: rgba(0,0,0,0.4); z-index: 1100; }
  .footer-content { flex-direction: column; text-align: center; gap: 8px; }
  .seller-footer { padding: 12px 14px; }
}
@media (max-width: 768px) {
  .header-utility-bar { padding: 4px 12px; font-size: 10.5px; }
  .utility-right { gap: 10px; }
  .current-time { display: none; }
  .header-main { padding: 0 12px; height: 46px; }
  .brand-icon { width: 30px; height: 30px; font-size: 16px; }
  .brand-text { display: none; }
  .content-wrapper { padding: 10px; }
  /* 모바일: 사이드바 링크 여백 촘촘하게 */
  .sidebar-link { padding: 9px 14px; font-size: 12.5px; margin-bottom: 0; }
  .tree-dashboard { padding: 9px 12px; }
}
@media (max-width: 576px) {
  .utility-left { display: none; }
  .seller-name span { display: none; }
  .footer-brand-text { display: none; }
}

.sidebar-nav::-webkit-scrollbar { width: 6px; }
.sidebar-nav::-webkit-scrollbar-thumb { background: #c0c0c0; border-radius: 0; }
</style>
